import React, { FC, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { PaginateList } from 'src/app/common/types/common-types';
import PruTable from 'src/app/common/components/PruTable/PruTable';
import { getDefaultDisplayDate } from 'src/app/common/utils';
import ItemDeleteDialog from 'src/app/common/components/ItemDeleteDialog';
import { appendAlertItem, AlertType } from '@pruforce/common-adminweb-sdk';
import { DeduplicationRule } from 'src/app/modules/PulseLeads/types/deduplication-rule-types';
import { deleteDeduplicationRule } from 'src/app/modules/PulseLeads/network/deduplicationRuleCrud';
import { deduplicationRulePath } from '../../LeadDeduplicationRuleRoutes';

type DeduplicationRuleListProps = {
  isLoading: boolean;
  deduplicationRuleList?: PaginateList<DeduplicationRule>;
  onRefresh: () => void;
  onChangePage: (page: number, rowsPerPage: number) => void;
};

type DialogState = {
  open: boolean;
  id: string;
};

const DeduplicationRuleList: FC<DeduplicationRuleListProps> = ({
  isLoading,
  deduplicationRuleList,
  onRefresh,
  onChangePage,
}) => {
  const history = useHistory();
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });
  const dispatch = useDispatch();

  const [dialogState, setDialogState] = useState<DialogState>({
    open: false,
    id: '',
  });

  const onDelete = async (id: string) => {
    try {
      await deleteDeduplicationRule(id, dispatch);
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.SUCCESS,
            title: 'Success',
            content: `Deduplication Rule deleted successfully - ${id}`,
          },
        ]),
      );
    } catch (err) {}
  };

  return (
    <>
      <ItemDeleteDialog
        key={`delete-deduplication-rule-dialog-${dialogState.open}`}
        open={dialogState.open}
        onClose={() => setDialogState({ open: false, id: '' })}
        onRefresh={onRefresh}
        onDelete={() => onDelete(dialogState.id)}
      />
      <PruTable
        title={Translation('pulseleads.deduplicationRule.list')}
        headerBtnDef={[
          {
            color: 'primary',
            title: Translation('pulseleads.deduplicationRule.newRule'),
            onClick: () => history.push(`${deduplicationRulePath}/create`),
          },
        ]}
        disableBulkSelect
        operationDef={[
          {
            title: Translation('section.common.operation.edit'),
            tooltipText: 'Edit Deduplication Rule',
            onClick: (row) => history.push(`${deduplicationRulePath}/edit/${row._id}`),
          },
          {
            title: Translation('section.common.operation.delete'),
            tooltipText: 'Delete Deduplication Rule',
            onClick: (row) => setDialogState({ open: true, id: row._id }),
          },
        ]}
        columnDef={[
          {
            isId: true,
            hidden: true,
            keyIndex: '_id',
            displayName: '',
            renderData: (row) => '',
          },
          {
            keyIndex: 'name',
            displayName: Translation('pulseleads.deduplicationRule.common.name'),
            renderData: (row) => row.name || '-',
          },
          {
            keyIndex: 'description',
            align: 'center',
            displayName: Translation('pulseleads.deduplicationRule.common.description'),
            renderData: (row) => row.description || '-',
          },
          {
            keyIndex: 'updatedBy',
            align: 'center',
            displayName: Translation('pulseleads.deduplicationRule.common.updatedBy'),
            renderData: (row) => row.updatedBy || '-',
          },
          {
            keyIndex: 'createdAt',
            align: 'center',
            displayName: Translation('pulseleads.deduplicationRule.common.createdAt'),
            renderData: (row) => getDefaultDisplayDate(new Date(row.createdAt), 'datetime'),
          },
          {
            keyIndex: 'updatedAt',
            align: 'center',
            displayName: Translation('pulseleads.deduplicationRule.common.updatedAt'),
            renderData: (row) => getDefaultDisplayDate(new Date(row.updatedAt), 'datetime'),
          },
        ]}
        isLoading={isLoading}
        onRefresh={onRefresh}
        dataSource={deduplicationRuleList?.docs}
        totalPages={deduplicationRuleList?.totalPages}
        totalRecords={deduplicationRuleList?.totalDocs}
        onChangePage={onChangePage}
      />
    </>
  );
};

export default DeduplicationRuleList;
